import { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Snackbar, Alert,
  Tab, Tabs, TextField, Typography, CircularProgress,
} from '@mui/material';
import { BugReport, Lightbulb, ExpandMore, ExpandLess, Edit } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { bugSeverities, featurePriorities } from '../../constants/statusOptions';
import { collectMetadata, getMetadataSummary } from '../../utils/errorCapture';
import { submitFeedback, getUploadUrl, uploadToBlob } from '../../services/feedbackService';
import { FeedbackAttachments } from './FeedbackAttachments';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';
import { useScreenshotCapture } from './useScreenshotCapture';
import type { FeedbackTicketType } from '../../types/feedback';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const FeedbackModal = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const [tab, setTab] = useState<FeedbackTicketType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('Medium');
  const [priority, setPriority] = useState('Nice to Have');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [metaOpen, setMetaOpen] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [annotatorImage, setAnnotatorImage] = useState<string | null>(null);

  const { screenshotUrl, capturing, resetScreenshot } = useScreenshotCapture(open);

  const resetForm = useCallback(() => {
    setTitle(''); setDescription(''); setSeverity('Medium'); setPriority('Nice to Have');
    setFiles([]); setError(''); setMetaOpen(false);
    setAnnotatorOpen(false); setAnnotatorImage(null); resetScreenshot();
  }, [resetScreenshot]);

  const handleClose = useCallback(() => {
    if (title.trim() || description.trim() || files.length) {
      if (!window.confirm('Discard your feedback?')) return;
    }
    resetForm(); onClose();
  }, [title, description, files, onClose, resetForm]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(new File([file], `clipboard-${Date.now()}-${i}.png`, { type: file.type }));
      }
    }
    if (imageFiles.length) { e.preventDefault(); setFiles((prev) => [...prev, ...imageFiles].slice(0, 10)); }
  }, []);

  const handleSubmit = useCallback(async () => {
    const finalTitle = title.trim() || `${tab === 'bug' ? 'Bug Report' : 'Feature Request'} — ${new Date().toLocaleString()}`;
    setSubmitting(true); setError('');
    try {
      const attachments: { FileName: string; BlobUrl: string; FileSize: number; ContentType: string }[] = [];
      for (const file of files) {
        const { uploadUrl, blobPath } = await getUploadUrl(file.name, file.type);
        await uploadToBlob(uploadUrl, file);
        attachments.push({ FileName: file.name, BlobUrl: blobPath, FileSize: file.size, ContentType: file.type });
      }
      const metadata = collectMetadata();
      const ticket = await submitFeedback({
        Type: tab, Title: finalTitle, Description: description.trim() || undefined,
        Severity: tab === 'bug' ? severity as any : undefined,
        Priority: tab === 'feature' ? priority as any : undefined,
        Attachments: attachments.length ? attachments : undefined, ...metadata,
      });
      resetForm(); onClose();
      setToast({ open: true, message: `${tab === 'bug' ? 'Bug' : 'Feature request'} received — ${ticket.TicketNumber}` });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally { setSubmitting(false); }
  }, [tab, title, description, severity, priority, files, onClose, resetForm]);

  const metaSummary = useMemo(() => open ? getMetadataSummary() : [], [open]);

  return (
    <>
      <Dialog open={open}
        onClose={(_, reason) => {
          // Prevent accidental closure from clicking outside the dialog
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth={false}
        className="feedback-modal-root"
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.25)' } },
          paper: { sx: { width: '92vw', height: '90vh', maxWidth: 1200, display: 'flex', flexDirection: 'column' } },
        }}
        sx={{ zIndex: 1500 }} onPaste={handlePaste}>

        <DialogTitle sx={{ pb: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <BugReport sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
          Report Bug / Request Feature
        </DialogTitle>

        <DialogContent sx={{ pb: 1, display: 'flex', gap: 2.5, overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {/* Left column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto', minWidth: 0 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ minHeight: 36, flexShrink: 0, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}>
              <Tab icon={<BugReport sx={{ fontSize: '1rem' }} />} iconPosition="start" label="Bug Report"
                value="bug" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<Lightbulb sx={{ fontSize: '1rem' }} />} iconPosition="start" label="Feature Request"
                value="feature" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
            </Tabs>

            <TextField label="Title (optional)" fullWidth size="small" value={title}
              onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 200 }}
              placeholder="Short summary — leave blank to auto-generate" sx={{ flexShrink: 0 }} />

            <TextField label="Description" fullWidth multiline size="small" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={tab === 'bug'
                ? 'Describe the issue...\n\n• What were you doing?\n• What did you expect?\n• What actually happened?\n\nPaste screenshots with Ctrl+V anywhere'
                : 'Describe the feature...\n\n• What should it do?\n• Why is it useful?\n\nPaste mockups/examples with Ctrl+V'}
              sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important' } }} />

            <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              {tab === 'bug' && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Severity</InputLabel>
                  <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value)}
                    MenuProps={{ sx: { zIndex: 1600 } }}>
                    {bugSeverities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {tab === 'feature' && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}
                    MenuProps={{ sx: { zIndex: 1600 } }}>
                    {featurePriorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          {/* Right column */}
          <Box sx={{ width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto' }}>
            {/* Screenshot */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Page Screenshot
              </Typography>
              {capturing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Capturing...</Typography>
                </Box>
              ) : screenshotUrl ? (
                <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
                  <Box component="img" src={screenshotUrl}
                    sx={{ display: 'block', width: '100%', cursor: 'pointer' }}
                    onClick={() => { setAnnotatorImage(screenshotUrl); setAnnotatorOpen(true); }} />
                  <Button size="small" startIcon={<Edit sx={{ fontSize: '0.85rem' }} />}
                    onClick={() => { setAnnotatorImage(screenshotUrl); setAnnotatorOpen(true); }}
                    sx={{ position: 'absolute', bottom: 6, right: 6, textTransform: 'none', fontSize: '0.7rem',
                      bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' }, borderRadius: 1, px: 1, py: 0.3 }}>
                    Annotate & Attach
                  </Button>
                </Box>
              ) : <Typography variant="caption" color="text.disabled">Screenshot not available</Typography>}
            </Box>

            {/* Attachments */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Attachments
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, fontWeight: 400 }}>
                  Ctrl+V / drag & drop / upload
                </Typography>
              </Typography>
              <FeedbackAttachments files={files} onChange={setFiles} maxFiles={10} />
            </Box>

            {/* Context */}
            <Box>
              <Button size="small" onClick={() => setMetaOpen(!metaOpen)}
                endIcon={metaOpen ? <ExpandLess /> : <ExpandMore />}
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', px: 0 }}>
                Auto-collected context ({metaSummary.length} items)
              </Button>
              <Collapse in={metaOpen}>
                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.04), borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`, maxHeight: 250, overflow: 'auto' }}>
                  {metaSummary.map((item) => (
                    <Box key={item.label} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem', color: 'text.secondary', display: 'block' }}>{item.label}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', display: 'block', lineHeight: 1.4 }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>

        {error && <Typography color="error" variant="caption" sx={{ px: 3, pb: 0.5 }}>{error}</Typography>}

        <DialogActions sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <Button onClick={handleClose} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} size="small" sx={{ textTransform: 'none' }}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {annotatorImage && (
        <ScreenshotAnnotator open={annotatorOpen} imageUrl={annotatorImage}
          onSave={(file) => { setFiles((prev) => [...prev, file].slice(0, 10)); setAnnotatorOpen(false); setAnnotatorImage(null); }}
          onCancel={() => { setAnnotatorOpen(false); setAnnotatorImage(null); }} />
      )}

      <Snackbar open={toast.open} autoHideDuration={5000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast({ open: false, message: '' })}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
};
